import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Flame, Trophy, Send, Target, Loader2, ExternalLink } from 'lucide-react';
import { apiRequest } from '@/lib/api-request';

interface ChatUserPopoverProps {
  userId: string;
  username: string;
  avatarUrl?: string;
  children: React.ReactNode;
}

interface UserDailyStats {
  userId: string;
  username: string;
  avatarImageUrl?: string;
  createdAt?: string;
  stats: {
    currentStreak: number;
    longestStreak: number;
    totalWins: number;
    totalSubmissions: number;
    totalVotesReceived: number;
    winRate: number;
  };
}

export function ChatUserPopover({ userId, username, avatarUrl, children }: ChatUserPopoverProps) {
  const [open, setOpen] = useState(false);

  const { data: userStats, isLoading } = useQuery<UserDailyStats>({
    queryKey: [`/api/daily/user/${userId}/stats`],
    enabled: open,
    staleTime: 60 * 1000, // Cache for 1 minute
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start" side="top">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : userStats ? (
          <div>
            {/* Header */}
            <div className="flex items-center gap-3 p-3 border-b">
              <Avatar className="h-10 w-10">
                <AvatarImage src={userStats.avatarImageUrl || avatarUrl} alt={userStats.username || username} />
                <AvatarFallback className="text-sm bg-primary/10">
                  {(userStats.username || username || '?').slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{userStats.username || username}</p>
                {userStats.stats.currentStreak > 0 && (
                  <div className="flex items-center gap-1 text-orange-500">
                    <Flame className="h-3 w-3" />
                    <span className="text-xs font-medium">{userStats.stats.currentStreak}-day streak</span>
                  </div>
                )}
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-px bg-muted">
              <StatItem
                icon={<Trophy className="h-3.5 w-3.5 text-yellow-500" />}
                label="Wins"
                value={userStats.stats.totalWins}
              />
              <StatItem
                icon={<Send className="h-3.5 w-3.5 text-green-500" />}
                label="Submissions"
                value={userStats.stats.totalSubmissions}
              />
              <StatItem
                icon={<Target className="h-3.5 w-3.5 text-purple-500" />}
                label="Win Rate"
                value={`${userStats.stats.winRate}%`}
              />
              <StatItem
                icon={<Flame className="h-3.5 w-3.5 text-orange-500" />}
                label="Best Streak"
                value={userStats.stats.longestStreak}
              />
            </div>

            {/* View profile link */}
            <Link
              href={`/profile/${userId}`}
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-1.5 p-2.5 text-xs font-medium text-primary hover:bg-muted transition-colors border-t"
            >
              View Full Profile
              <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        ) : (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Could not load stats
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function StatItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <div className="bg-background p-2.5 flex items-center gap-2">
      {icon}
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-semibold">{value}</p>
      </div>
    </div>
  );
}
